import { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";

import {
  Category,
  CategoryPayload,
} from "../types";

interface Props {
  open: boolean;
  loading?: boolean;
  initialData?: Category | null;
  onClose: () => void;
  onSubmit: (data: CategoryPayload) => void;
}

const emptyForm: CategoryPayload = {
  name: "",
  description: "",
  remarks: "",
  icon: "",
  color: "",
  displayOrder: 0,
};

const CategoryFormDialog = ({
  open,
  loading = false,
  initialData,
  onClose,
  onSubmit,
}: Props) => {
  const [form, setForm] = useState<CategoryPayload>(emptyForm);

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name,
        description: initialData.description || "",
        remarks: initialData.remarks || "",
        icon: initialData.icon || "",
        color: initialData.color || "",
        displayOrder: initialData.displayOrder || 0,
      });
      return;
    }

    setForm(emptyForm);
  }, [initialData, open]);

  const handleChange = (
    field: keyof CategoryPayload,
    value: string | number
  ) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    onSubmit({
      ...form,
      name: form.name.trim(),
      description: form.description?.trim() || undefined,
      remarks: form.remarks?.trim() || undefined,
      icon: form.icon?.trim() || undefined,
      color: form.color?.trim() || undefined,
      displayOrder: Number(form.displayOrder || 0),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {initialData ? "Edit Category" : "Create Category"}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} mt={1}>
          <TextField
            label="Category Name"
            value={form.name}
            onChange={(event) =>
              handleChange("name", event.target.value)
            }
            fullWidth
            required
          />

          <TextField
            label="Description"
            value={form.description || ""}
            onChange={(event) =>
              handleChange("description", event.target.value)
            }
            fullWidth
            multiline
            rows={3}
          />

          <TextField
            label="Remarks"
            value={form.remarks || ""}
            onChange={(event) =>
              handleChange("remarks", event.target.value)
            }
            fullWidth
          />

          <TextField
            label="Icon"
            value={form.icon || ""}
            onChange={(event) =>
              handleChange("icon", event.target.value)
            }
            fullWidth
          />

          <TextField
            label="Color"
            value={form.color || ""}
            onChange={(event) =>
              handleChange("color", event.target.value)
            }
            fullWidth
          />

          <TextField
            label="Display Order"
            type="number"
            value={form.displayOrder ?? 0}
            onChange={(event) =>
              handleChange("displayOrder", Number(event.target.value))
            }
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || form.name.trim().length < 2}
        >
          {initialData ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryFormDialog;
